import { useState, useEffect } from "react";
import {
  Collapse,
  Button,
  Row,
  Col,
  Tooltip,
  Popconfirm,
} from "antd";

import {
  MinusOutlined,
  PlusOutlined,
  FileTextOutlined, // submitted
  FileSyncOutlined, // in progress
  FileSearchOutlined, // in review
  FileDoneOutlined, //placed
  ExceptionOutlined, // exited
  FileUnknownOutlined, // withdrawn
} from "@ant-design/icons";
import { useParams, useLocation, useNavigate } from "react-router-dom";

import PersonalInformation from "./PersonalInformation";
import Qualification from "./Qualification";
import SupportingDocument from "./SupportingDocument";
import PlacementInformation from "./PlacementInformation";

import service from "../../services/nysService";
import { recordError } from "../../utils/logService";

import {
  Messages,
  Notify,
  openNotification,
  util,
} from '@seims/shared';

const { Panel } = Collapse;

const ViewCandidate = () => {
  const [currentStatus, setCurrentStatus] = useState("");
  const [statusColour, setStatusColour] = useState("");
  const [statusIcon, setStatusIcon] = useState();
  const [demographics, setDemographics] = useState({});
  const [qualification, setQualification] = useState({});
  const [exitDetails, setExitDetails] = useState({});
  const [documents, setDocuments] = useState([]);
  const [candidateUserId, setCandidateUserId] = useState("");
  const [disabledWithdraw, setDisableWithdraw] = useState(false);
  const [intake, setIntake] = useState();
  const [localStore, setLocalStore] = useState({});

  useEffect(() => {
    prePopulate();
    statusColourSwitch(currentStatus);
  }, [currentStatus, disabledWithdraw]);

  // const placementStatus = (receivingStatus) => {
  //   setStatusData(receivingStatus);
  // };

  const username = demographics.name + " " + demographics.surname;
  const { key } = useParams();
  const location = useLocation();
  let navigate = useNavigate();
  let path = `/candidate/:key`;

  const prePopulate = async () => {
    try {

      if (key) {
        const id = key.substring(key.indexOf("x") + 1, key.length);

        if (!location?.state) {
          openNotification(
            "Candidates",
            "System is redirecting to candidate list",
            Notify.WARNING
          );
          navigate("/candidate", { replace: true });

          return;
        }

        const { selectedIntake } = location?.state?.candidateslist;
        setLocalStore(location?.state?.candidateslist);
        setCandidateUserId(id);
        setIntake(selectedIntake);

        const response = await service.getCandidateById(id, selectedIntake);
        // if (!location?.state) {
        //   openNotification(
        //     "Candidates",
        //     "System is redirecting to candidate list",
        //     Notify.INFO
        //   );
        //   navigate("/candidate", { replace: true });

        //   return;
        // }

        if (response && response.status === 200) {
          const { data } = response;
          if (data.demographics !== undefined)
            setDemographics(data.demographics);
          if (data.qualifications !== undefined)
            setQualification(data.qualifications);
          if (data.supportingDocuments !== undefined)
            setDocuments(data.supportingDocuments);
          if (data.placementStatus !== undefined)
            setCurrentStatus(data.placementStatus?.value);
          if (data.exitDetails !== undefined) {
            setExitDetails(data.exitDetails);
          }
        }
      }
    } catch (error) {
      recordError(error);
    }
  };

  const statusColourSwitch = (status) => {
    switch (status.trim()) {
      case "Submitted":
        setStatusColour("status-submitted");
        setStatusIcon(<FileTextOutlined />);
        break;
      case "In Progress":
        setStatusColour("status-progress");
        setStatusIcon(<FileSyncOutlined />);
        break;
      case "In Review":
        setStatusColour("status-review");
        setStatusIcon(<FileSearchOutlined />);
        break;
      case "Placed":
        setStatusColour("status-placed");
        setStatusIcon(<FileDoneOutlined />);
        break;
      case "Exited":
        setStatusColour("status-exited");
        setStatusIcon(<ExceptionOutlined />);
        break;
      case "Withdrawn":
        setStatusColour("status-withdrawn");
        setStatusIcon(<FileUnknownOutlined />);
        break;
      default:
        setStatusColour("");
    }
  };

  const handleWithdrawSubmit = async () => {
    let msg;

    try {
      const { selectedIntake } = location.state.candidateslist;
      const response = await service.candidateWithdraw(
        candidateUserId,
        selectedIntake
      );

      if (response && (response.status === 200 || response.status === 204)) {
        setDisableWithdraw(true);
        msg = util.parseMessage(Messages.UPDATE, "Application");
        openNotification("Withdraw Application", msg, Notify.SUCCESS);
        return;
      }
    } catch (error) {
      msg = util.parseMessage(Messages.UPDATE_FAILURE, "Withdraw application");
      openNotification("Withdraw Application", msg, Notify.ERROR);
      recordError(error);
    }
  };

  const returnHome = () => {
    const state = location?.state;
    if (state) {
      const { from } = state;
      navigate(from);

      const { currentPage, pageSize, selectedIntake } =
        location.state?.candidateslist;
      navigate(from, {
        state: {
          currentPage: currentPage,
          pageSize: pageSize,
          selectedIntake: selectedIntake,
        },
      });
    } else {
      openNotification(
        "Candidates",
        "System is redirecting to candidate list",
        Notify.WARNING
      );
      navigate("/candidate", { replace: true });

      return;
    }
  };

  const handleCancel = () => {
    returnHome();
  };

  const onBackClick = () => {
    returnHome();
  };

  const items = [
    {
    key: '1',
    label: 'Personal Information',
    children: <PersonalInformation data={demographics} />,
  },
  {
    key: '2',
    label: 'Qualifications',
    children: <Qualification data={qualification} />,
  },
  {
    key: '3',
    label: 'Supporting Documents',
    children: <SupportingDocument data={documents} />,
  },
  {
    key: '4',
    label: 'Placement Information',
    children: (
      <PlacementInformation
        currentStatus={currentStatus}
        candidate={location.state?.candidateslist}
        setCurrentStatus={setCurrentStatus}
        userId={candidateUserId}
        intake={intake}
        exitDetails={exitDetails}
      />
    ),
  }
  ];

  return (
    <div className="content">
      <Row>
        <Col xs={24} sm={24} md={8}>
          <h2>Candidate: {username}</h2>
        </Col>
        <Col xs={24} sm={8} md={8} className="center-align">
          <div className={statusColour + " status-label"}>
            <div>
              <span className="status-icon">{statusIcon}</span>&nbsp;{" "}
              {currentStatus}
            </div>
          </div>
        </Col>
        <Col xs={24} sm={8} md={8} style={{ textAlign: "right" }}>
          {/* <Link key="1" to="/candidate"> */}
          <Button type="default" onClick={onBackClick}>
            Back
          </Button>
          {/* </Link> */}
        </Col>
      </Row>
      <br />
      <Collapse
        className="nys-panel"
        defaultActiveKey={["1"]}
        expandIcon={({ isActive }) =>
          isActive ? <MinusOutlined /> : <PlusOutlined />
        }
        expandIconPosition="end"
        items={items}
      />
        {/* <Panel header="Personal Information" key="1">
          <PersonalInformation data={demographics} />
        </Panel>
        <Panel header="Qualifications" key="2">
          <Qualification data={qualification} />
        </Panel>
        <Panel header="Supporting Documents" key="3">
          <SupportingDocument data={documents} />
        </Panel>
        <Panel header="Placement Information" key="4">
          <PlacementInformation
            currentStatus={currentStatus}
            candidate={location.state?.candidateslist}
            setCurrentStatus={setCurrentStatus}
            userId={candidateUserId}
            intake={intake}
            exitDetails={exitDetails}
          />
        </Panel> */}

        {/* {value === "Placed" && (
            <Panel header="Candidate Behaviour" key="5">
              <CandidateBehaviour userId={candidateUserId} />
            </Panel>
          )}
          {value === "Exited" && (
            <Panel header="Candidate Behaviour" key="5">
              <CandidateBehaviour userId={candidateUserId} />
            </Panel>
          )} */}
    
      <br />
      {/* <Row>
          <p><b>Previously applied? {}</b></p>
      </Row> */}
      {currentStatus !== "Placed" &&
        currentStatus !== "Exited" &&
        currentStatus !== "Withdrawn" && (
          <Row>
            <Col xs={24} className="right-align">
              <Tooltip key="2tooltip">
                {!disabledWithdraw && (
                  <Popconfirm
                    title={
                      <p>
                        By clicking yes, you agree that this application will no
                        longer be considered
                        <br />
                        for the NYS programme and cannot be resubmitted for this
                        application round,
                        <br /> proceed?
                      </p>
                    }
                    onConfirm={() => handleWithdrawSubmit()}
                    onCancel={() => handleCancel()}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button
                      key="3b"
                      type="danger"
                      htmlType="submit"
                      className="right-align-button"
                      style={{ marginLeft: "40px", color: "#f2f2f2" }}
                    >
                      Withdraw Applicant
                    </Button>
                  </Popconfirm>
                )}
              </Tooltip>
            </Col>
          </Row>
        )}

      {/* {(currentStatus === "In Progress") && (
        <Row>
          <Col xs={24} className="right-align">
            <Tooltip key="2tooltip">
              {(!disabledWithdraw) && (
                <Popconfirm
                  title={
                    <p>
                      By clicking yes, you agree that you will no longer be
                      considered
                      <br />
                      for the NYS programme and cannot resubmit your application
                      for this application round,
                      <br /> proceed?
                    </p>
                  }
                  // onConfirm={() => handleWithdrawSubmit()}
                  onConfirm={() => handleUnsuccessful()}
                  onCancel={() => handleCancel()}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button
                    key="3b"
                    type="danger"
                    htmlType="submit"
                    className="right-align-button"
                    style={{ marginLeft: "40px", color: "#f2f2f2" }}
                  >
                    Unsuccessful Applicant
                  </Button>
                </Popconfirm>
              )}
            </Tooltip>
          </Col>
        </Row>
      )} */}
    </div>
  );
};

export default ViewCandidate;
